import React, { useState, useEffect, useContext } from "react";
import { Box, Slider, IconButton, Typography, Fade, CircularProgress, MenuItem, Popover, Dialog, DialogTitle, DialogContent, TextField, DialogActions,} from "@mui/material";
import { PlayArrow, Pause, Forward10, Replay10, VolumeUp, Fullscreen,PictureInPictureAlt} from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import SpeedIcon from "@mui/icons-material/Speed";
import VolumeOff from "@mui/icons-material/VolumeOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmailIcon from "../../assets/common/emailIcon.svg";
import '../../styles/videoPlayer.css';
import ContentCutIcon from "@mui/icons-material/ContentCut";
import CollateralService from "../../services/collaterals/CollateralService";
import {
  buildVideoClipFilename,
  CLIP_DEFAULT_DURATION_SEC,
  CLIP_DOWNLOAD_MAX_SEC,
  CLIP_MAX_DURATION_MESSAGE,
  downloadBlob,
  formatClipDuration,
  formatClipTimeInput,
  getClipActionState,
  parseBlobError,
  parseClipTimeInput,
  secondsToHHMMSS,
} from "../../utils/utils";
import FileIcon from "../../assets/interaction-detail-page/file-icon.svg";
import { GlobalContext } from "../../context/GlobalContext";
import { getUserAttributes } from "../../utils/auth";
import ProgressSpinner from "../Common/ProgressSpinner";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";

type ClipRangeRequest = {
  start: number;
  end?: number;
  id: number;
};

type Props = {
    videoRef: React.RefObject<HTMLVideoElement | HTMLAudioElement>;
    hideDownloadAndPiP?: boolean;
    onClippingChange?: React.Dispatch<React.SetStateAction<boolean>>
    videoAsset?: any
    interactionId?: string
    interactionDetails?: any
    spritesheetUrl?: string
    assetId?: string;
    isAudio?: boolean;
    clipRangeRequest?: ClipRangeRequest | null;
};  

export default function CustomVideoControls({ videoRef , hideDownloadAndPiP,
   onClippingChange, videoAsset, interactionId, interactionDetails, spritesheetUrl, assetId, isAudio, clipRangeRequest}: Props) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(100);
  const [showVolume, setShowVolume] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsMenuLevel, setSettingsMenuLevel] = useState<"root" | "speed">("root");
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [muted, setMuted] = useState(false);
  const [isClipping, setIsClipping] = useState(false);
  const [clipRange, setClipRange] = useState<[number, number]>([0, 0]);
  const [windowStart, setWindowStart] = useState(0); // Start time of the 5-min window
  const [openDialogBox, setOpenDialogBox] = useState<boolean>(false);
  const { featureMap } = useContext(GlobalContext);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [description,setDescription] = useState("");
  const [subject, setSubject] = useState("");
  const [emailError, setEmailError] = useState("");
  const windowDuration = 300; // 5 minutes in seconds
  const scrollMargin = 2;
  const framesCount = 10;
  const isClippingEnabled = featureMap?.idp_clipping === true;
  const userInfo = getUserAttributes();
  const loggedInUserName = userInfo?.name;
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const { showSnackbar } = useDisplaySnackbar();
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [speedAnchorEl, setSpeedAnchorEl] = useState<null | HTMLElement>(null);
  const videoDownloadEnabled = featureMap?.video_download === true;
  const { impersonationDetails } = useContext(GlobalContext);
  const isImpersonating = impersonationDetails?.isImpersonating === true;

  const clipFileName = `${interactionDetails?.title || "Untitled"}.mp4`;
  const clipDuration = clipRange[1] - clipRange[0];
  const { isOverDownloadMax: isClipOverMax, canShare, canDownload } = getClipActionState(clipDuration);
  const isShareDisabled = !canShare;
  const showDownloadButton = videoDownloadEnabled && !isImpersonating && !isAudio;
  const isDownloadDisabled = showSpinner || !canDownload;
  const defaultClipDuration = CLIP_DEFAULT_DURATION_SEC;
  const maxClientDownloadSec = CLIP_DOWNLOAD_MAX_SEC;
  const [startTimeInput, setStartTimeInput] = useState("");
  const [endTimeInput, setEndTimeInput] = useState("");
  const [isEditingStartTime, setIsEditingStartTime] = useState(false);
  const [isEditingEndTime, setIsEditingEndTime] = useState(false);
  const [clipTimeError, setClipTimeError] = useState("");
  const isClippingRef = React.useRef(isClipping);
  const clipRangeRef = React.useRef(clipRange);
  const durationRef = React.useRef(duration);
  const windowStartRef = React.useRef(windowStart);
  const clipDragRef = React.useRef<{
    lastX: number;
    stripWidth: number;
    visibleLen: number;
  } | null>(null);
  isClippingRef.current = isClipping;
  clipRangeRef.current = clipRange;
  durationRef.current = duration;
  windowStartRef.current = windowStart;

  const { mutate: downloadVideoClip } = CollateralService.usePostVideoClipDownload({
    onMutate: () => {
      setShowSpinner(true);
    },
    onSuccess: (result) => {
      if (!(result?.blob instanceof Blob) || result.blob.size === 0) {
        showSnackbar("error", "Clip file was not returned");
        setShowSpinner(false);
        return;
      }
      downloadBlob(result.blob, result.filename || "clip.mp4", result.blob.type || "video/mp4");
      showSnackbar("success", "Clip downloaded successfully");
      setShowSpinner(false);
    },
    onError: async (err) => {
      console.error("Failed to download clip:", err);
      const message = await parseBlobError(err, "Failed to download clip");
      showSnackbar("error", message);
      setShowSpinner(false);
    },
  });

  const handleDownloadClip = () => {
    if (!showDownloadButton || isDownloadDisabled) return;

    const clipAssetId = videoAsset?.interactionAssetId || assetId;
    if (!interactionId || !clipAssetId) {
      showSnackbar("error", "Unable to download clip. Missing video details.");
      return;
    }

    const meetingTitle = interactionDetails?.title || "Untitled";
    downloadVideoClip({
      interactionId,
      assetId: clipAssetId,
      body: {
        title: clipFileName,
        recipientEmail: userInfo?.email || "",
        videoStartTime: secondsToHHMMSS(clipRange[0]),
        videoEndTime: secondsToHHMMSS(clipRange[1]),
        emailSubject: "",
        emailDescription: "",
        loggedInUserName: loggedInUserName || "Unknown User",
        videoClipName: buildVideoClipFilename(meetingTitle),
      },
    });
  };

  const openSpeedMenu = (e: React.MouseEvent<HTMLElement>) => {
    setSpeedAnchorEl(e.currentTarget);
  };

  const closeSpeedMenu = () => {
    setSpeedAnchorEl(null);
  };

  const { mutate: shareVideoClip } = CollateralService.usePostVideoClipping({
    onMutate: () => {
      setShowSpinner(true);
    },
    onSuccess: () => {
      showSnackbar(
        "success",
        `Video clipping request processed successfully. Clip sent to ${recipientEmail}`
      );
      setShowSpinner(false);
      setOpenDialogBox(false);
      setRecipientEmail("");
      setSubject("");
      setDescription("");
      setEmailError("");
      setIsClipping(false);
    },
    onError: async (err) => {
      console.error("Failed to create clip:", err);
      const message = await parseBlobError(err, "Failed to create clip");
      showSnackbar("error", message);
      setShowSpinner(false);
    },
  });
  

  useEffect(() => {
    if (!isClipping) {
      setClipTimeError("");
    }
    if (!isEditingStartTime) {
      setStartTimeInput(formatClipTimeInput(clipRange[0]));
    }
    if (!isEditingEndTime) {
      setEndTimeInput(formatClipTimeInput(clipRange[1]));
    }
  }, [clipRange, isClipping, isEditingStartTime, isEditingEndTime]);

  const clampWindowStart = (value: number, dur = durationRef.current || 0) =>
    Math.max(0, Math.min(Math.max(dur - windowDuration, 0), value));

  const applyClipRange = (
    newStart: number,
    explicitEnd?: number,
    forceAlign = false,
    enforceMax = true
  ) => {
    const dur = durationRef.current || 0;
    const [prevStart, prevEnd] = clipRangeRef.current;
    const prevLen = prevEnd > prevStart ? prevEnd - prevStart : defaultClipDuration;
    const maxStart = dur > 0 ? dur : Math.max(newStart, 0);
    const clampedStart = Math.max(0, Math.min(maxStart, newStart));
    let clampedEnd =
      explicitEnd != null && Number.isFinite(explicitEnd)
        ? explicitEnd
        : clampedStart + prevLen;
    if (clampedEnd <= clampedStart) {
      clampedEnd = clampedStart + Math.max(prevLen, 5);
    }
    if (dur > 0) {
      clampedEnd = Math.min(dur, clampedEnd);
    }
    if (enforceMax && clampedEnd - clampedStart > maxClientDownloadSec) {
      clampedEnd = clampedStart + maxClientDownloadSec;
    }
    if (clampedEnd - clampedStart < 1) {
      clampedEnd = dur > 0 ? Math.min(dur, clampedStart + 5) : clampedStart + 5;
    }

    clipRangeRef.current = [clampedStart, clampedEnd];
    setClipRange([clampedStart, clampedEnd]);

    const visibleStart = windowStartRef.current;
    const needsAlign =
      forceAlign ||
      clampedStart < visibleStart + scrollMargin ||
      clampedEnd > visibleStart + windowDuration - scrollMargin;
    if (needsAlign) {
      const nextWindow = clampWindowStart(clampedStart - 1, dur);
      windowStartRef.current = nextWindow;
      setWindowStart(nextWindow);
    }

    return clampedStart;
  };

  useEffect(() => {
    if (!clipRangeRequest || !isClippingRef.current) return;
    applyClipRange(clipRangeRequest.start, undefined, true);
  }, [clipRangeRequest]);

  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const onLoadedMetadata = () => setDuration(video.duration);
    const onTimeUpdate = () => {
      const now = video.currentTime;
      setCurrentTime(now);
      if (video.seeking) return;

      if (isClippingRef.current) {
        const [start, end] = clipRangeRef.current;
        if (end > start && now >= end) {
          video.pause();
          video.currentTime = end;
          setCurrentTime(end);
        }
      }
    };
    const onSeeked = () => {
      const now = video.currentTime;
      setCurrentTime(now);
      if (!isClippingRef.current) return;
      const [start, end] = clipRangeRef.current;
      if (now + 0.2 < start || now - 0.2 > end) {
        applyClipRange(now);
      }
    };
    const onPlay = () => {
      setIsPlaying(true);
      setBuffering(false);
      if (video.seeking) return;
      if (isClippingRef.current) {
        const [clipStart, clipEnd] = clipRangeRef.current;
        if (clipEnd > clipStart && video.currentTime >= clipEnd - 0.05) {
          video.currentTime = clipStart;
          setCurrentTime(clipStart);
        }
      }
    };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);

    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTypingInInput =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
  
      if (isTypingInInput) return; // allow space when typing
  
      if (e.code === "Space") {
        e.preventDefault();
        video.paused ? video.play() : video.pause();
      }
    };
    window.addEventListener("keydown", handleKey);

    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      window.removeEventListener("keydown", handleKey);
    };
  }, [videoRef.current?.src]);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
    const s = Math.floor(seconds % 60).toString().padStart(2, "0");
    if (h > 0) {
      return `${h}:${m}:${s}`; 
    }
    return `${m}:${s}`; 
  };  

  const moveClipRangeTo = (newStart: number) => applyClipRange(newStart);

  const seekToClipStart = (start: number) => {
    if (videoRef?.current) {
      videoRef.current.currentTime = start;
    }
    setCurrentTime(start);
  };

  const shiftClipBy = (deltaSeconds: number) => {
    if (!deltaSeconds) return;
    const [start, end] = clipRangeRef.current;
    const len = end > start ? end - start : defaultClipDuration;
    const dur = durationRef.current || 0;
    const maxStart = Math.max(0, (dur || start + len) - len);
    const nextStart = Math.max(0, Math.min(maxStart, start + deltaSeconds));
    const nextEnd = Math.min(dur > 0 ? dur : nextStart + len, nextStart + len);

    clipRangeRef.current = [nextStart, nextEnd];
    setClipRange([nextStart, nextEnd]);
    setClipTimeError("");

    let nextWindow = windowStartRef.current;
    if (nextEnd > nextWindow + windowDuration - scrollMargin) {
      nextWindow = clampWindowStart(nextEnd - (windowDuration - scrollMargin), dur);
    } else if (nextStart < nextWindow + scrollMargin) {
      nextWindow = clampWindowStart(nextStart - scrollMargin, dur);
    }
    if (nextWindow !== windowStartRef.current) {
      windowStartRef.current = nextWindow;
      setWindowStart(nextWindow);
    }

    seekToClipStart(nextStart);
  };

  const getTimelineTimeFromEvent = (event: React.PointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const visibleLen = Math.min(
      windowDuration,
      Math.max((durationRef.current || 0) - windowStartRef.current, 0.0001)
    );
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    return windowStartRef.current + ratio * visibleLen;
  };

  const handleTimelinePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest(".MuiSlider-thumb")) {
      return;
    }

    const time = getTimelineTimeFromEvent(event);
    const [start, end] = clipRangeRef.current;
    const inSelection = time >= start && time <= end;
    const rect = event.currentTarget.getBoundingClientRect();
    const visibleLen = Math.min(
      windowDuration,
      Math.max((durationRef.current || 0) - windowStartRef.current, 0.0001)
    );

    if (inSelection) {
      clipDragRef.current = {
        lastX: event.clientX,
        stripWidth: rect.width,
        visibleLen,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    const newStart = moveClipRangeTo(time);
    seekToClipStart(newStart);
    setClipTimeError("");
  };

  const handleTimelinePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!clipDragRef.current) return;
    const { lastX, stripWidth, visibleLen } = clipDragRef.current;
    const deltaSec = ((event.clientX - lastX) / stripWidth) * visibleLen;
    clipDragRef.current.lastX = event.clientX;
    shiftClipBy(deltaSec);
  };

  const handleTimelinePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (clipDragRef.current) {
      clipDragRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
  };

  const handleSeek = (_: any, value: number | number[]) => {
    if (!videoRef?.current || typeof value !== "number") return;
    const seekTime = isClipping ? moveClipRangeTo(value) : value;
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleSkip = (seconds: number) => {
    if (!videoRef?.current) return;
    const newTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + seconds));
    const seekTime = isClipping ? moveClipRangeTo(newTime) : newTime;
    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleVolumeChange = (_: any, value: number | number[]) => {
    if (!videoRef?.current || typeof value !== "number") return;
    videoRef.current.volume = value / 100;
    setVolume(value);
    setMuted(value === 0);
  };

  const toggleMute = () => {
    if (!videoRef?.current) return;
  
    if (muted || volume === 0) {
      videoRef.current.volume = 1;
      setVolume(100);
      setMuted(false);
    } else {
      videoRef.current.volume = 0;
      setVolume(0);
      setMuted(true);
    }
  };  

  const handlePictureInPicture = () => {
    const video = videoRef?.current;
    // Ensure the current element is a HTMLVideoElement before calling video-specific API
    if (
      video &&
      document.pictureInPictureEnabled &&
      video instanceof HTMLVideoElement &&
      typeof video.requestPictureInPicture === "function"
    ) {
      video.requestPictureInPicture().catch(console.error);
    }
  };

  useEffect(() => {
    if (onClippingChange) {
      onClippingChange(isClipping);
    }
  }, [isClipping, onClippingChange]);

  useEffect(() => {
    if (spritesheetUrl) {
      const img = new Image();
      img.src = spritesheetUrl;
    }
  }, [spritesheetUrl]);
  
  useEffect(() => {
    if (videoRef?.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const handleClipRangeChange = (_: Event, value: number | number[]) => {
    if (!Array.isArray(value)) return;
  
    let [startRel, endRel] = value;
    let newStart = startRel + windowStart;
    let newEnd = endRel + windowStart;
  
    // Clamp
    newStart = Math.max(0, Math.min(duration, newStart));
    newEnd = Math.max(0, Math.min(duration, newEnd));
    if (newEnd - newStart > maxClientDownloadSec) {
      newEnd = newStart + maxClientDownloadSec;
      setClipTimeError(CLIP_MAX_DURATION_MESSAGE);
    } else {
      setClipTimeError("");
    }
  
    // Dynamically shift window when dragging outside visible range
    let newWindowStart = windowStart;
  
    if (newEnd > windowStart + windowDuration - scrollMargin) {
      newWindowStart = clampWindowStart(newEnd - (windowDuration - scrollMargin));
    } else if (newStart < windowStart + scrollMargin) {
      newWindowStart = clampWindowStart(newStart - scrollMargin);
    }
  
    setWindowStart(newWindowStart);
    windowStartRef.current = newWindowStart;
    clipRangeRef.current = [newStart, newEnd];
    setClipRange([newStart, newEnd]);
  
    if (videoRef?.current) videoRef.current.currentTime = newStart;
  };

  const commitStartTime = () => {
    const parsedStart = parseClipTimeInput(startTimeInput);
    if (parsedStart === null) {
      setStartTimeInput(formatClipTimeInput(clipRange[0]));
      setEndTimeInput(formatClipTimeInput(clipRange[1]));
      return;
    }

    const dur = durationRef.current || 0;
    const start = Math.max(0, dur > 0 ? Math.min(dur, parsedStart) : parsedStart);
    applyClipRange(start, undefined, true);
    seekToClipStart(start);
    const nextEnd = clipRangeRef.current[1];
    setStartTimeInput(formatClipTimeInput(start));
    setEndTimeInput(formatClipTimeInput(nextEnd));
    setClipTimeError(
      nextEnd - start > maxClientDownloadSec ? CLIP_MAX_DURATION_MESSAGE : ""
    );
  };

  const commitEndTime = () => {
    const parsedEnd = parseClipTimeInput(endTimeInput);
    if (parsedEnd === null) {
      setEndTimeInput(formatClipTimeInput(clipRange[1]));
      return;
    }

    const start = clipRange[0];
    const dur = durationRef.current || 0;
    const end = Math.max(0, dur > 0 ? Math.min(dur, parsedEnd) : parsedEnd);

    if (end <= start) {
      setClipTimeError("End time must be after start time");
      return;
    }

    applyClipRange(start, end, true, false);
    seekToClipStart(start);
    setEndTimeInput(formatClipTimeInput(end));
    setClipTimeError(
      end - start > maxClientDownloadSec ? CLIP_MAX_DURATION_MESSAGE : ""
    );
  };  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const handleSubmit = () => {
     // --- Validation before submitting ---
     if (!canShare) {
      showSnackbar("error", "Only clips up to 3 min can be shared via email");
      return;
    }
     if (!recipientEmail) {
      setEmailError("Recipient email is required");
      return;
    } else if (!emailRegex.test(recipientEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // clear error if valid
    setEmailError("");
    const meetingTitle = interactionDetails?.title || "Untitled";
    const payload = {
      interactionId: interactionId || "",
      assetId: videoAsset?.interactionAssetId || assetId,
      body: {
        title: clipFileName,
        recipientEmail,
        videoStartTime: secondsToHHMMSS(clipRange[0]),
        videoEndTime: secondsToHHMMSS(clipRange[1]),
        emailSubject: subject,
        emailDescription: description,
        loggedInUserName: loggedInUserName || "Unknown User",
        videoClipName: buildVideoClipFilename(meetingTitle),
      },
    };
    shareVideoClip(payload);
  };

  const FRAME_WIDTH = 160;  
  const FRAME_HEIGHT = 90;  
  const getIntervalByDuration = (durationSeconds: number) => {
    if (durationSeconds <= 10 * 60) return 3;
    if (durationSeconds <= 30 * 60) return 5;
    if (durationSeconds <= 60 * 60) return 8;
    return 10;
  };
  const SPRITE_COLS = 24;
  const interval = getIntervalByDuration(duration);
  const totalFrames = Math.ceil(duration / interval);
  const SPRITE_ROWS = Math.ceil(totalFrames / SPRITE_COLS);
  const frameIndex = Math.min(
    totalFrames - 1,
    hoverTime !== null ? Math.floor(hoverTime / interval) : 0
  );
  const row = Math.floor(frameIndex / SPRITE_COLS);
  const col = frameIndex % SPRITE_COLS;  
  const backgroundX = -col * FRAME_WIDTH;
  const backgroundY = -row * FRAME_HEIGHT;
    
  return (
    <Box className="video-controls-root">
      <Box
        className="slider-wrapper"
        onMouseMove={(e) => {
          const sliderRect = e.currentTarget.getBoundingClientRect();
          const percent = (e.clientX - sliderRect.left) / sliderRect.width;
          const newHoverTime = duration * percent;
          setHoverTime(Math.min(duration, Math.max(0, newHoverTime)));
          setHoverX(e.clientX - sliderRect.left);
        }}
        onMouseLeave={() => {
          setHoverTime(null);
          setHoverX(null);
        }}
      >
        <Slider
          value={duration ? currentTime : 0}
          max={duration || 1}
          onChange={handleSeek}
          sx={{
            color: "#7341FF",
            height: 5,
            borderRadius: 1,
            mb: 0.5,
            mt: -1,
            "& .MuiSlider-thumb": {
              width: 10,
              height: 10,
              backgroundColor: "#fff",
              border: "2px solid #7341FF",
              "&:hover": { boxShadow: "0 0 0 4px rgba(115,65,255,0.2)" },
            },
          }}
        />
        {hoverTime !== null && hoverX !== null && !isAudio && (
          <Box
            className="hover-frame-preview"
            style={{
              left: `${hoverX}px`,
              transform: "translateX(-50%)",
              position: "absolute",
              bottom: "2.5rem",
              width: `${FRAME_WIDTH}px`,
              height: `${FRAME_HEIGHT}px`,
              overflow: "hidden",
              borderRadius: "6px",
              backgroundColor: "#000",
            }}
          >
            <Box
              style={{
                width: `${FRAME_WIDTH}px`,
                height: `${FRAME_HEIGHT}px`,
                backgroundImage: `url(${spritesheetUrl})`,
                backgroundPosition: `${backgroundX}px ${backgroundY}px`,
                backgroundSize: `${SPRITE_COLS * FRAME_WIDTH}px ${SPRITE_ROWS * FRAME_HEIGHT}px`,
              }}
            />
            {!spritesheetUrl && (
              <Box className="no-preview-text">
                No preview Available
              </Box>
            )}
            <Box className="hover-time-badge">
              {formatTime(hoverTime)}
            </Box>
          </Box>
        )}
      </Box>

      <Box className="controls-row">
        <Typography className="time-label" variant="caption">
          {formatTime(currentTime)}
        </Typography>

        <Box
          className="volume-container"
          onMouseEnter={() => setShowVolume(true)}
          onMouseLeave={() => setShowVolume(false)}
        >
          <Box onClick={toggleMute}>
            {volume === 0 ? (
              <VolumeOff
                sx={{ fontSize: 20, color: "#666", cursor: "pointer" }}
              />
            ) : (
              <VolumeUp
                sx={{ fontSize: 20, color: "#666", cursor: "pointer" }}
              />
            )}
          </Box>
          <Fade in={showVolume} timeout={300} unmountOnExit>
            <Box className="volume-popup">
              <Slider
                orientation="vertical"
                value={volume}
                onChange={handleVolumeChange}
                sx={{
                  height: 80,
                  color: "#7341FF",
                  "& .MuiSlider-thumb": { width: 8, height: 8 },
                  marginBottom: -1,
                }}
              />
            </Box>
          </Fade>
        </Box>

        <IconButton
          size="small"
          onClick={() => handleSkip(-10)}
        >
          <Replay10 sx={{ fontSize: 24, color: "#5D06C9" }} />
        </IconButton>

        <IconButton
          size="small"
          className="play-button"
          onClick={() => {
            if (!videoRef?.current) return;
            videoRef?.current?.paused
              ? videoRef?.current?.play()
              : videoRef?.current?.pause();
          }}
        >
          {buffering ? (
            <CircularProgress size={18} thickness={4} />
          ) : isPlaying ? (
            <Pause />
          ) : (
            <PlayArrow />
          )}
        </IconButton>

        <IconButton
          size="small"
          onClick={() => handleSkip(10)}
        >
          <Forward10 sx={{ fontSize: 24, color: "#5D06C9" }} />
        </IconButton>

        <Typography className="time-label" variant="caption">
          {formatTime(duration)}
        </Typography>

        { isClippingEnabled && !isImpersonating && (
          <IconButton
          size="small"
          onClick={() => {
            if (!videoRef?.current) return;
            const minClipDuration = defaultClipDuration;
            const start = videoRef.current.currentTime || 0;
            const end = Math.min(start + minClipDuration, duration || start + minClipDuration);
            if (!isClipping) {
              const newWindowStart = clampWindowStart(start - 1, duration);
              windowStartRef.current = newWindowStart;
              clipRangeRef.current = [start, end];
              setWindowStart(newWindowStart);
              setClipRange([start, end]);
              setClipTimeError("");
              setIsClipping(true);
              videoRef.current.currentTime = start;
            } else {
              setIsClipping(false);
              setClipRange([0, 0]);
            }
          }}
          >
          <ContentCutIcon
            sx={{ fontSize: 20, color: isClipping ? "#7341FF" : "#666" }}
            />
        </IconButton>
          )}

        <Popover
          open={Boolean(settingsAnchorEl)}
          anchorEl={settingsAnchorEl}
          onClose={() => {
            setSettingsAnchorEl(null);
            setSettingsMenuLevel("root");
          }}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          PaperProps={{ className: "settings-popover" }}
        >
          {settingsMenuLevel === "root" ? (
            <Box>
              {!hideDownloadAndPiP && (
                <MenuItem
                  onClick={() => {
                    if (videoRef?.current?.src) {
                      const a = document.createElement("a");
                      a.href = videoRef?.current?.src;
                      a.download = "video.mp4";
                      a.click();
                    }
                    setSettingsAnchorEl(null);
                  }}
                >
                  <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
                  Download
                </MenuItem>
              )}

              <MenuItem onClick={() => setSettingsMenuLevel("speed")}>
                <SpeedIcon fontSize="small" sx={{ mr: 1 }} />
                Playback Speed
              </MenuItem>

              {!hideDownloadAndPiP && (
                <MenuItem
                  onClick={() => {
                    handlePictureInPicture();
                    setSettingsAnchorEl(null);
                  }}
                >
                  <PictureInPictureAlt fontSize="small" sx={{ mr: 1 }} />
                  Picture-in-Picture
                </MenuItem>
              )}
            </Box>
          ) : (
            <Box>
              {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                <MenuItem
                  key={speed}
                  onClick={() => {
                    if (videoRef?.current) {
                      videoRef.current.playbackRate = speed;
                    }
                    setSettingsAnchorEl(null);
                    setSettingsMenuLevel("root");
                  }}
                  className="speed-menu-item"
                >
                  {speed}x
                </MenuItem>
              ))}
            </Box>
          )}
        </Popover>
  
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography className="playback_rate_text" onClick={openSpeedMenu}>
            {playbackRate}x 
          </Typography>
          {videoDownloadEnabled && !isImpersonating &&(
            <IconButton
              size="small"
              onClick={() => {
                if (videoRef?.current?.src) {
                  const a = document.createElement("a");
                  a.href = videoRef.current.src;
                  a.download = "video.mp4";
                  a.click();
                }
              }}
            >
              <DownloadIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
        <Popover
          open={Boolean(speedAnchorEl)}
          anchorEl={speedAnchorEl}
          onClose={closeSpeedMenu}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          PaperProps={{ className: "settings-popover" }}
        >
          {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
            <MenuItem
              key={speed}
              selected={playbackRate === speed}
              onClick={() => {
                setPlaybackRate(speed);
                closeSpeedMenu();
              }}
            >
              {speed}×
            </MenuItem>
          ))}
        </Popover>
        
        {videoRef.current instanceof HTMLVideoElement && (
          <IconButton size="small" onClick={() => videoRef.current?.requestFullscreen()}>
            <Fullscreen sx={{ fontSize: 20, color: "#666" }} />
          </IconButton>
        )}
      </Box>
      {isClipping && (
        <Box className="clip-timeline-container" mt={2}>
          {/* Time markers */}
          <Box className="clip-time-markers">
            {Array.from({ length: 5 }).map((_, i) => {
              const fraction = i / 4;
              const time = windowStart + fraction * windowDuration;
              return (
                <Box key={i} className={`clip-marker-section ${i === 4 ? 'last-marker' : ''}`}>
                  {/* Time label */}
                  <Typography className="clip-time-marker">
                    {formatTime(Math.min(time, duration))}
                  </Typography>

                  {/* Dots between markers, not after the last one */}
                  {i < 4 && (
                    <Box className="clip-dots">
                      {Array.from({ length: 4 }).map((_, dotIndex) => (
                        <Box key={dotIndex} className="clip-dot" />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Timeline strip with video thumbnails */}
          <Box
            className="clip-timeline-strip"
            onPointerDown={handleTimelinePointerDown}
            onPointerMove={handleTimelinePointerMove}
            onPointerUp={handleTimelinePointerUp}
            onPointerCancel={handleTimelinePointerUp}
          >
            {/* ADD YOUR VIDEO THUMBNAILS HERE */}
            <Box className="clip-thumbnails">
            {Array.from({ length: framesCount }).map((_, i) => {
              const frameTime = Math.min(
                duration || windowStart + windowDuration,
                windowStart + (windowDuration / Math.max(framesCount - 1, 1)) * i
              );

              const frameIndex = Math.min(
                totalFrames - 1,
                Math.floor(frameTime / interval)
              );
              const row = Math.floor(frameIndex / SPRITE_COLS);
              const col = frameIndex % SPRITE_COLS;

              return (
                <Box
                  key={i}
                  className="clip-thumbnail-box"
                >
                  <Box
                    className="clip-thumbnail-sprite"
                    style={{
                      width: `${FRAME_WIDTH}px`,
                      height: `${FRAME_HEIGHT}px`,
                      backgroundImage: `url(${spritesheetUrl})`,
                      backgroundPosition: `${-col * FRAME_WIDTH}px ${-row * FRAME_HEIGHT}px`,
                      backgroundSize: `${SPRITE_COLS * FRAME_WIDTH}px ${SPRITE_ROWS * FRAME_HEIGHT}px`,
                    }}
                  />
                </Box>
              );
              })}
            </Box>

            {/* Range Slider - THIS GOES ON TOP OF THUMBNAILS */}
            <Box className="clip-slider-container">
              <Slider
                value={(() => {
                  const visibleMax = Math.min(windowDuration, Math.max(duration - windowStart, 0));
                  const start = Math.max(0, Math.min(visibleMax, clipRange[0] - windowStart));
                  const end = Math.max(start, Math.min(visibleMax, clipRange[1] - windowStart));
                  return [start, end];
                })()}
                min={0}
                max={Math.min(windowDuration, Math.max(duration - windowStart, 0))}
                className="clip-slider"
                onChange={handleClipRangeChange}
                disableSwap
              />
            </Box>
          </Box>

          {/* Duration / max-clip message */}
          <Box className={`clip-duration-display ${clipTimeError || isClipOverMax ? "error" : ""}`}>
            {clipTimeError || formatClipDuration(clipRange[1] - clipRange[0])}
          </Box>
          <Box className="clipping-actions-container">
            {/* Top row: Time display boxes & Download + Share Clip buttons */}
            <Box className="clipping-actions-top">
              {/* Time display boxes */}
              <Box className="clip-time-boxes">
                <Box className="clip-time-box">
                  <input
                    className="clip-time-input"
                    value={startTimeInput}
                    aria-label="Clip start time"
                    onChange={(e) => setStartTimeInput(e.target.value)}
                    onFocus={() => setIsEditingStartTime(true)}
                    onBlur={() => {
                      commitStartTime();
                      setIsEditingStartTime(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </Box>

                <Typography className="clip-time-separator">–</Typography>

                <Box className={`clip-time-box ${isClipOverMax || clipTimeError ? "error" : ""}`}>
                  <input
                    className="clip-time-input"
                    value={endTimeInput}
                    aria-label="Clip end time"
                    onChange={(e) => setEndTimeInput(e.target.value)}
                    onFocus={() => setIsEditingEndTime(true)}
                    onBlur={() => {
                      commitEndTime();
                      setIsEditingEndTime(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Action buttons (Download & Share Clip) */}
              <Box className="clip-action-buttons">
                {showDownloadButton && (
                <button
                  className="clip-button download"
                  onClick={handleDownloadClip}
                  disabled={isDownloadDisabled}
                >
                  <DownloadIcon sx={{ fontSize: 18, mr: 0.5 }} />
                  Download
                </button>
                )}

                <button
                  className="clip-button share"
                  disabled={isShareDisabled}
                  onClick={() => {
                    if (!isShareDisabled) {
                      setOpenDialogBox(true);
                    }
                  }}
                >
                  <img src={EmailIcon} alt="email" />
                  Share Clip
                </button>
              </Box>
            </Box>

            {/* Bottom row: Info message pill & Cancel button */}
            <Box className="clipping-actions-bottom">
              <Box className="clip-info-pill">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#7341FF", mr: 0.5 }} />
                <Typography className="clip-info-text">
                  Only clips up to 3 min can be shared via email
                </Typography>
              </Box>

              <button
                className="clip-button cancel"
                onClick={() => {
                  setIsClipping(false);
                  setClipRange([0, 0]);
                }}
              >
                Cancel
              </button>
            </Box>
          </Box>
        </Box>
      )}
        <Dialog open={openDialogBox} onClose={() => setOpenDialogBox(false)} className="share-clip-dialog-box"
          PaperProps={{
            className: "share-clip-paper",
          }}>
          <DialogTitle>
            <h1 className="dialog-box-title baseText">Share Clip</h1>
          </DialogTitle>
          <DialogContent>
            <Box className="dialog-content-container">
              
              {/* File display */}
              <Box className="file-display">
              <Box className="file-info">
                <img src={FileIcon} alt="file" className="file-icon" />
                <Typography
                  className="file-name baseText"
                  title={clipFileName}
                >
                  {clipFileName}
                </Typography>
              </Box>
                <Typography className="file-size baseText">{formatTime(clipRange[0])} - {formatTime(clipRange[1])}</Typography>
              </Box>

              {/* Recipient input */}
              <TextField
            placeholder="To"
            value={recipientEmail}
            onChange={(e) => {
              setRecipientEmail(e.target.value);
              setEmailError(""); // clear on typing
            }}
            fullWidth
            required
            error={!!emailError}
            helperText={emailError}
            className="input-field"
          />

              {/* Subject input */}
              <TextField
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                fullWidth
                className="input-field"
              />

              {/* Note / description */}
              <TextField
                placeholder="Add a note or description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                className="input-field multiline-field"
              />

            </Box>
          </DialogContent>

          <DialogActions className="dialog-actions">
            <div className="dialog-actions-container">
            <button onClick={() => setOpenDialogBox(false)} className="clip-button cancel">
              Cancel
            </button>
            <button onClick={handleSubmit} className={`clip-button send-button ${(!recipientEmail || !!emailError) ? "send-button-disabled" : ""}`} disabled={!recipientEmail || !!emailError}>
              Send
            </button>
            </div>
          </DialogActions>
        </Dialog>
            <ProgressSpinner showSpinner ={showSpinner}/>
    </Box>
  );  
  
}
